import { useState, useEffect } from "react";
import { Grid3x3 } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ServiceSubcategory {
  id: string;
  subcategory_key: string;
  name_en: string;
  name_zh: string;
  description_en?: string;
  description_zh?: string;
  domain_id?: string;
  sort_order: number;
}

interface SubcategoryFilterProps {
  selectedDomain: string;
  selectedSubcategory: string;
  onSubcategoryChange: (subcategoryKey: string) => void;
  onDomainChange?: (domainKey: string) => void;
}

const SubcategoryFilter = ({ 
  selectedDomain, 
  selectedSubcategory, 
  onSubcategoryChange,
  onDomainChange
}: SubcategoryFilterProps) => {
  const { t, i18n } = useTranslation('practitioners');
  const [subcategories, setSubcategories] = useState<ServiceSubcategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Fetch service subcategories from Supabase
  useEffect(() => {
    const fetchSubcategories = async () => {
      setIsLoading(true);
      try {
        let query = supabase
          .from('service_subcategories')
          .select('*, effective_domains!inner(domain_key, sort_order)')
          .eq('is_active', true);

        // Filter by domain if selected
        if (selectedDomain && selectedDomain !== 'all_domains') {
          query = query.eq('effective_domains.domain_key', selectedDomain);
        }

        const { data, error } = await query
          .order('sort_order', { foreignTable: 'effective_domains', ascending: true })
          .order('sort_order', { ascending: true });

        if (error) throw error;

        // Client-side stable sorting as fallback (身→灵→意→境 order)
        const domainOrder = ['body', 'vitality', 'consciousness', 'context'];
        const sorted = (data || []).sort((a: any, b: any) => {
          const da = a.effective_domains?.domain_key;
          const db = b.effective_domains?.domain_key;
          const ia = domainOrder.indexOf(da);
          const ib = domainOrder.indexOf(db);
          if (ia !== ib) return ia - ib;
          return (a.sort_order ?? 0) - (b.sort_order ?? 0);
        });
        
        setSubcategories(sorted || []);
      } catch (error) {
        console.error("Error fetching subcategories:", error);
        toast({
          variant: 'destructive',
          title: "Failed to load subcategories",
          description: "Please try refreshing the page.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubcategories();
  }, [selectedDomain, toast]);

  // Reset subcategory when domain changes
  useEffect(() => {
    if (selectedDomain === 'all_domains') {
      onSubcategoryChange('all_subcategories');
    }
  }, [selectedDomain, onSubcategoryChange]);

  // Get display name based on current language
  const getDisplayName = (subcategory: ServiceSubcategory) => {
    if (i18n.language === 'zh') {
      return subcategory.name_zh;
    }
    return subcategory.name_en;
  };

  // Handle subcategory selection with auto domain linkage
  const handleSubcategorySelect = (subcategoryKey: string) => {
    onSubcategoryChange(subcategoryKey);
    
    // Auto-select corresponding domain when a specific subcategory is chosen
    if (subcategoryKey !== 'all_subcategories' && onDomainChange) {
      const selectedSub = subcategories.find(s => s.subcategory_key === subcategoryKey);
      const domainKey = (selectedSub as any)?.effective_domains?.domain_key;
      if (domainKey) {
        onDomainChange(domainKey);
      }
    }
  };

  return (
    <div>
      <h3 className="text-sm font-semibold mb-2 flex items-center">
        <Grid3x3 className="mr-2 h-4 w-4 text-amber-600" /> {t('subdomain')}
      </h3>
      
      <Select 
        value={selectedSubcategory} 
        onValueChange={handleSubcategorySelect}
        disabled={isLoading}
      >
        <SelectTrigger className="border-amber-200">
          <SelectValue placeholder={t('allSubdomains')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all_subcategories">{t('allSubdomains')}</SelectItem>
          {subcategories.map((subcategory) => (
            <SelectItem key={subcategory.id} value={subcategory.subcategory_key}>
              {getDisplayName(subcategory)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default SubcategoryFilter;
